import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../firebase";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useShoppingCart } from "./ShoppingCartProvider";

const PaymentConfirmation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();
  const userID = user ? user.uid : null;
  const { clearCart } = useShoppingCart();

  useEffect(() => {
    // Extract orderID from URL
    if (!userID) {
      setError("User ID not found");
      setLoading(false);
      return;
    }
    const queryParams = new URLSearchParams(location.search);
    const orderID = queryParams.get("orderID"); // PayPal orderID

    if (!orderID) {
      setError("Missing orderID");
      setLoading(false);
      return;
    }

    // Payment has already been confirmed by PayPal button's onApprove function
    // Just clear the cart and navigate to success page
    const handlePaymentSuccess = async () => {
      try {
        // Clear the shopping cart after successful payment
        clearCart();

        // Navigate to success page
        navigate("/payment-success", { state: { orderID } });
      } catch (error: any) {
        console.error("Payment confirmation error:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    handlePaymentSuccess();
  }, [location, navigate, userID, clearCart]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        textAlign="center"
      >
        {/* Payment Processing Spinner */}
        <CircularProgress
          size={80}
          thickness={4}
          color="primary"
          sx={{ mb: 4 }}
        />

        {/* Heading */}
        <Typography variant="h4" component="h1" gutterBottom>
          Processing Your Payment
        </Typography>

        {/* Subtext */}
        <Typography variant="body1" color="textSecondary">
          Please wait while we complete your payment. This may take a moment.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return <div>Payment confirmed! Redirecting to the success page...</div>;
};

export default PaymentConfirmation;
